import React, { useCallback, useEffect, useState } from "react";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { Checkbox } from "../../../../ui/components/Checkbox";
import { Select } from "../../../../ui/components/Select";
import { TextField } from "../../../../ui/components/TextField";
import { TextArea } from "../../../../ui/components/TextArea";
import {
  FeatherEdit2,
  FeatherPlus,
  FeatherRefreshCw,
  FeatherSparkles,
} from "@subframe/core";
import {
  fetchTDSRates,
  updateTdsCuration,
  suggestTdsLlmContext,
  fetchTdsSections,
  createTdsInZoho,
  updateTdsInZoho,
  TDSRate,
  TdsSectionType,
} from "../../../../services/api";

interface EditorState {
  tax_name: string;
  tax_percentage: string;
  tax_specific_type: string;
  section: string;
  threshold: string;
  business_display: boolean;
  individual_display: boolean;
  llm_context: string;
}

/**
 * The threshold column is NOT NULL and 0 is a real value ("no threshold — always
 * deduct", e.g. section 195), so the editor shows an empty box for 0 rather than
 * a literal "0" the user has to clear. Both round-trip to 0.
 */
const thresholdToInput = (value: number | undefined): string =>
  value === undefined || value === null || value === 0 ? "" : String(value);

const emptyEditor = (): EditorState => ({
  tax_name: "",
  tax_percentage: "",
  tax_specific_type: "",
  section: "",
  threshold: "",
  business_display: false,
  individual_display: false,
  llm_context: "",
});

const editorStateFor = (rate: TDSRate): EditorState => ({
  tax_name: rate.tds_tax_name || "",
  tax_percentage: String(rate.tds_tax_percentage ?? ""),
  tax_specific_type: rate.tax_specific_type || "",
  section: rate.section || "",
  threshold: thresholdToInput(rate.threshold),
  business_display: rate.business_display,
  individual_display: rate.individual_display,
  llm_context: rate.llm_context || "",
});

const zohoFieldsChanged = (rate: TDSRate, e: EditorState): boolean =>
  e.tax_name !== (rate.tds_tax_name || "") ||
  e.tax_percentage !== String(rate.tds_tax_percentage ?? "") ||
  e.tax_specific_type !== (rate.tax_specific_type || "");

const curationChanged = (rate: TDSRate, e: EditorState): boolean =>
  e.section !== (rate.section || "") ||
  e.threshold !== thresholdToInput(rate.threshold) ||
  e.business_display !== rate.business_display ||
  e.individual_display !== rate.individual_display ||
  e.llm_context !== (rate.llm_context || "");

const needsCuration = (rate: TDSRate): boolean =>
  !rate.section &&
  !rate.business_display &&
  !rate.individual_display &&
  !rate.llm_context;

const byCurationThenName = (a: TDSRate, b: TDSRate): number => {
  const aN = needsCuration(a) ? 0 : 1;
  const bN = needsCuration(b) ? 0 : 1;
  if (aN !== bN) return aN - bN;
  return a.tds_tax_name.localeCompare(b.tds_tax_name);
};

/**
 * Manage TDS rates: create + edit rates in Zoho Books (write-through, mirrored
 * locally) and curate the local-only fields. Active rows expose an editable
 * "Zoho Books" zone (name, rate, nature of payment); expired-era rows keep it
 * read-only. Rendered inside the "Manage TDS Rates" modal on the Sync tab.
 */
export const TdsCurationList: React.FC = () => {
  const [rates, setRates] = useState<TDSRate[]>([]);
  const [sections, setSections] = useState<TdsSectionType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [saving, setSaving] = useState(false);
  const [rewriting, setRewriting] = useState(false);
  const [savedFlashId, setSavedFlashId] = useState<number | null>(null);
  // Whether the current AI-context value was auto-filled from the section
  // description (so re-picking a section refreshes it, but a manual edit or an
  // AI rewrite is never clobbered).
  const [ctxAutoFilled, setCtxAutoFilled] = useState(false);

  const loadRates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchTDSRates();
      setRates(result.data.tds_rates);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load TDS rates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRates();
  }, [loadRates]);

  // The section catalogue is seeded reference data, so it is fetched once and
  // deliberately left out of the reload path.
  useEffect(() => {
    fetchTdsSections(2)
      .then((rows) => {
        setSections(rows);
        if (rows.length === 0) {
          setError(
            "Section catalogue is empty — run the tds_section_types migration.",
          );
        }
      })
      .catch((err) =>
        setError(
          `Could not load the section catalogue: ${
            err instanceof Error ? err.message : "unknown error"
          }. If this is an authentication error, sign in again.`,
        ),
      );
  }, []);

  const activeRates = rates
    .filter((r) => !r.is_expired)
    .sort(byCurationThenName);
  const expiredRates = rates.filter((r) => r.is_expired).sort(byCurationThenName);
  const uncuratedCount = rates.filter(needsCuration).length;
  // The new-era classifier selects `WHERE is_expired = 0 AND llm_context <> ''`
  // (tds_classification_service.php), so a current-era rate with no AI context is
  // invisible to it — and if nothing is current-era, it has no options at all.
  // needsCuration() does not catch either case: a rate with a section and a
  // display flag counts as curated while still being unusable by the classifier.
  const activeWithoutContext = activeRates.filter(
    (r) => !r.llm_context || r.llm_context.trim() === "",
  ).length;

  const closeAll = () => {
    setExpandedId(null);
    setCreating(false);
    setEditor(null);
    setCtxAutoFilled(false);
  };

  const openCreate = () => {
    closeAll();
    setCreating(true);
    setEditor(emptyEditor());
    setCtxAutoFilled(false);
    setError(null);
    setNotice(null);
  };

  const openEditor = (rate: TDSRate) => {
    setCreating(false);
    setExpandedId(rate.id);
    setEditor(editorStateFor(rate));
    setCtxAutoFilled(false);
    setError(null);
    setNotice(null);
  };

  const handleCreate = async () => {
    if (!editor) return;
    setSaving(true);
    setError(null);
    try {
      const result = await createTdsInZoho({
        tax_name: editor.tax_name,
        tax_percentage: editor.tax_percentage,
        tax_specific_type: editor.tax_specific_type,
        section: editor.section,
        threshold: editor.threshold,
        business_display: editor.business_display,
        individual_display: editor.individual_display,
        llm_context: editor.llm_context,
      });
      setNotice(
        result.data.local_warning || "TDS rate created in Zoho and saved.",
      );
      closeAll();
      await loadRates();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create TDS rate");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async (rate: TDSRate) => {
    if (!editor) return;
    setSaving(true);
    setError(null);
    try {
      if (!rate.is_expired && zohoFieldsChanged(rate, editor)) {
        // Zoho-owned field changed → write through to Zoho (carries curation).
        const result = await updateTdsInZoho(rate.tds_tax_id, {
          tax_name: editor.tax_name,
          tax_percentage: editor.tax_percentage,
          tax_specific_type: editor.tax_specific_type,
          section: editor.section,
          threshold: editor.threshold,
          business_display: editor.business_display,
          individual_display: editor.individual_display,
          llm_context: editor.llm_context,
        });
        setNotice(
          result.data.versioned
            ? "This rate was in use, so Zoho expired it and created a new entry. Your curation was carried over."
            : result.data.local_warning || "TDS rate updated in Zoho.",
        );
      } else if (curationChanged(rate, editor)) {
        // Only local curation changed.
        await updateTdsCuration(rate.id, {
          section: editor.section,
          threshold: editor.threshold,
          business_display: editor.business_display,
          individual_display: editor.individual_display,
          llm_context: editor.llm_context,
        });
        setSavedFlashId(rate.id);
        setTimeout(() => setSavedFlashId(null), 2500);
      }
      closeAll();
      await loadRates();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleRewrite = async (rateId: number | null) => {
    if (!editor) return;
    setRewriting(true);
    setError(null);
    try {
      const result = await suggestTdsLlmContext(
        rateId !== null
          ? { id: rateId, draft: editor.llm_context }
          : {
              draft: editor.llm_context,
              tax_name: editor.tax_name,
              tax_percentage: editor.tax_percentage,
              tax_specific_type: editor.tax_specific_type,
              section: editor.section,
            },
      );
      setEditor((prev) =>
        prev ? { ...prev, llm_context: result.data.suggestion } : prev,
      );
      setCtxAutoFilled(false); // AI output is meaningful — don't let a section re-pick clobber it
    } catch (err) {
      setError(err instanceof Error ? err.message : "AI rewrite failed");
    } finally {
      setRewriting(false);
    }
  };

  // Shared field block for the Zoho-owned zone. Zoho itself refuses to update an
  // expired-era rate, so those stay read-only.
  const zohoReadOnlyReason = (isExpired: boolean): string | null =>
    isExpired ? "(read-only — expired era)" : null;

  const zohoZone = (isExpired: boolean) => {
    const reason = zohoReadOnlyReason(isExpired);
    const readOnly = reason !== null;
    return (
      editor && (
      <div className="flex w-full flex-col items-start gap-3">
        <span className="text-caption-bold font-caption-bold text-subtext-color">
          ZOHO BOOKS {reason}
        </span>
        <div className="flex w-full flex-wrap items-end gap-4">
          <TextField className="w-64" label="Tax name" helpText="">
            <TextField.Input
              disabled={readOnly}
              placeholder="e.g. Professional Fees"
              value={editor.tax_name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setEditor({ ...editor, tax_name: e.target.value })
              }
            />
          </TextField>
          <TextField className="w-28" label="Rate %" helpText="">
            <TextField.Input
              disabled={readOnly}
              placeholder="10"
              value={editor.tax_percentage}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setEditor({ ...editor, tax_percentage: e.target.value })
              }
            />
          </TextField>
        </div>
        <Select
          className="w-full"
          label="Nature of payment"
          placeholder="Select a section"
          helpText=""
          disabled={readOnly}
          value={editor.tax_specific_type || ""}
          onValueChange={(v: string) => {
            // Prefill the AI context from the section's DB description when the
            // box is empty or still holds an earlier auto-fill.
            const shouldFill =
              editor.llm_context.trim() === "" || ctxAutoFilled;
            const picked = sections.find((x) => x.tax_specific_type === v);
            setEditor({
              ...editor,
              tax_specific_type: v,
              llm_context:
                shouldFill && picked ? picked.description : editor.llm_context,
            });
            if (shouldFill && picked) setCtxAutoFilled(true);
          }}
        >
          {sections.map((s) => (
            <Select.Item key={s.tax_specific_type} value={s.tax_specific_type}>
              {s.description} ({s.section_no})
            </Select.Item>
          ))}
        </Select>
        {!readOnly && (
          <span className="text-caption font-caption text-amber-700">
            If this rate is already used on bills, Zoho will expire it and create
            a new entry when you change the rate. Your curation carries over.
          </span>
        )}
      </div>
      )
    );
  };

  // Shared field block for the zournal-only curation zone.
  const curationZone = (rateId: number | null) =>
    editor && (
      <div className="flex w-full flex-col items-start gap-3">
        <span className="text-caption-bold font-caption-bold text-subtext-color">
          ZOURNAL
        </span>
        {/* items-start, not items-end: the threshold field carries help text, and
            bottom-aligning would push its label a line above the others. */}
        <div className="flex w-full flex-wrap items-start gap-6">
          <TextField className="w-72" label="Section label" helpText="">
            <TextField.Input
              placeholder='e.g. "Section 393(1) Sl7"'
              value={editor.section}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setEditor({ ...editor, section: e.target.value })
              }
            />
          </TextField>
          <TextField
            className="w-48"
            label="Threshold (₹)"
            helpText="Blank = no threshold, always deduct"
          >
            <TextField.Input
              placeholder="e.g. 30000"
              value={editor.threshold}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setEditor({ ...editor, threshold: e.target.value })
              }
            />
          </TextField>
          {/* pt-7 lifts the boxes level with the inputs beside them, clearing the
              field labels above. */}
          <div className="flex items-center gap-6 pt-7">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={editor.business_display}
                onCheckedChange={(c) =>
                  setEditor({ ...editor, business_display: Boolean(c) })
                }
              />
              <span className="text-body font-body text-default-font">
                Companies
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={editor.individual_display}
                onCheckedChange={(c) =>
                  setEditor({ ...editor, individual_display: Boolean(c) })
                }
              />
              <span className="text-body font-body text-default-font">
                Individuals &amp; HUF
              </span>
            </div>
          </div>
        </div>
        {/* Deliberately says "marks", not "controls". These two columns are
            written and read back but nothing filters on them yet: no caller hits
            /api/get-tds/business or /individual, no cockpit-core file reads them,
            and the bill picker filters on document date and era only. Promising
            an effect they do not have would be a lie in the product copy. */}
        <span className="text-caption font-caption text-subtext-color">
          Marks which deductee the rate is meant for — 194C, for instance, is 1%
          for individuals and HUF but 2% for companies.
        </span>
        <TextArea
          className="w-full"
          label="AI context"
          helpText="Tells the AI classifier when this rate applies. Leave blank to keep it out of AI suggestions."
        >
          <TextArea.Input
            className="min-h-[72px]"
            placeholder="e.g. Fees for professional services: legal, medical, consultancy… Rate 10%."
            value={editor.llm_context}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              setEditor({ ...editor, llm_context: e.target.value });
              setCtxAutoFilled(false);
            }}
          />
        </TextArea>
        <Button
          variant="neutral-secondary"
          icon={<FeatherSparkles />}
          onClick={() => handleRewrite(rateId)}
          disabled={
            rewriting ||
            saving ||
            // Create mode: need a name (or draft) for the AI to work from.
            (rateId === null &&
              editor.tax_name.trim() === "" &&
              editor.llm_context.trim() === "")
          }
        >
          {rewriting
            ? "Rewriting..."
            : editor.llm_context.trim()
              ? "Rewrite with AI"
              : "Write with AI"}
        </Button>
      </div>
    );

  const renderRate = (rate: TDSRate) => {
    const expanded = expandedId === rate.id;
    return (
      <div
        key={rate.id}
        className="border-b border-solid border-neutral-border last:border-b-0"
      >
        <div
          className={`flex w-full items-center justify-between gap-3 px-4 py-2.5 ${
            rate.is_expired ? "opacity-60" : ""
          }`}
        >
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-body font-body text-default-font">
              {rate.tds_tax_name}
            </span>
            <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
              {rate.tds_tax_percentage}%
            </span>
            {rate.section ? (
              <span className="truncate text-caption font-caption text-subtext-color">
                {rate.section}
              </span>
            ) : (
              rate.tax_specific_type && (
                <span className="truncate text-caption font-caption text-subtext-color">
                  {rate.tax_specific_type}
                </span>
              )
            )}
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            {savedFlashId === rate.id && (
              <span className="text-caption font-caption text-green-700">
                Saved
              </span>
            )}
            {needsCuration(rate) && (
              <Badge variant="warning">Needs curation</Badge>
            )}
            {rate.business_display && <Badge variant="neutral">Companies</Badge>}
            {rate.individual_display && (
              <Badge variant="neutral">Individuals</Badge>
            )}
            <Button
              variant="neutral-tertiary"
              size="small"
              icon={<FeatherEdit2 />}
              onClick={() => (expanded ? closeAll() : openEditor(rate))}
            >
              {expanded ? "Close" : "Edit"}
            </Button>
          </div>
        </div>

        {expanded && editor && (
          <div className="flex w-full flex-col items-start gap-4 border-t border-solid border-neutral-border bg-neutral-50 px-4 py-4">
            {zohoZone(!!rate.is_expired)}
            {curationZone(rate.id)}
            <div className="flex w-full items-center justify-end gap-2">
              <Button
                variant="neutral-tertiary"
                onClick={closeAll}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="brand-primary"
                onClick={() => handleSaveEdit(rate)}
                disabled={
                  saving ||
                  rewriting ||
                  (!zohoFieldsChanged(rate, editor) &&
                    !curationChanged(rate, editor))
                }
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const sectionHeader = (title: string, count: number) => (
    <div className="sticky top-0 z-10 flex w-full items-center gap-2 border-b border-solid border-neutral-border bg-neutral-100 px-4 py-2">
      <span className="text-caption-bold font-caption-bold text-subtext-color">
        {title}
      </span>
      <Badge variant="neutral">{count}</Badge>
    </div>
  );

  const amber = (text: string) => (
    <span className="text-caption font-caption text-amber-700">{text}</span>
  );

  const headline = loading
    ? null
    : rates.length === 0
      ? null
      : activeRates.length === 0
        ? amber("No current-era rates — AI can't suggest TDS")
        : activeWithoutContext > 0
          ? amber(`${activeWithoutContext} without AI context`)
          : uncuratedCount > 0
            ? <Badge variant="warning">{uncuratedCount} need curation</Badge>
            : (
              <span className="text-caption font-caption text-subtext-color">
                All rates curated
              </span>
            );

  const canCreate =
    editor &&
    editor.tax_name.trim() !== "" &&
    editor.tax_percentage.trim() !== "" &&
    editor.tax_specific_type !== "";

  return (
    <div className="flex w-full flex-col items-start gap-3">
      <div className="flex w-full items-center justify-between">
        {/* One signal, not a stack of them. Most-blocking first: a list with no
            current-era rate cannot feed the AI classifier at all, which matters
            more than how many rows are half-curated. */}
        <div className="flex items-center gap-2">{headline}</div>
        <div className="flex items-center gap-2">
          <Button
            variant="neutral-secondary"
            size="small"
            icon={<FeatherRefreshCw />}
            onClick={loadRates}
            disabled={loading}
          >
            {loading ? "Loading..." : "Reload"}
          </Button>
          <Button
            variant="brand-primary"
            size="small"
            icon={<FeatherPlus />}
            onClick={openCreate}
            disabled={creating}
          >
            New TDS Rate
          </Button>
        </div>
      </div>

      {notice && (
        <div className="flex w-full flex-col items-start gap-2 rounded-lg border border-solid border-green-200 bg-green-50 p-3">
          <span className="text-body font-body text-green-800">{notice}</span>
        </div>
      )}
      {error && (
        <div className="flex w-full flex-col items-start gap-2 rounded-lg border border-solid border-red-200 bg-red-50 p-3">
          <span className="text-body font-body text-red-600">{error}</span>
        </div>
      )}

      {creating && editor && (
        <div className="flex w-full flex-col items-start gap-4 rounded-lg border border-solid border-brand-200 bg-brand-50 px-4 py-4">
          <span className="text-body-bold font-body-bold text-default-font">
            New TDS Rate
          </span>
          {zohoZone(false)}
          {curationZone(null)}
          <div className="flex w-full items-center justify-end gap-2">
            <Button
              variant="neutral-tertiary"
              onClick={closeAll}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="brand-primary"
              onClick={handleCreate}
              disabled={saving || !canCreate}
            >
              {saving ? "Creating..." : "Create in Zoho"}
            </Button>
          </div>
        </div>
      )}

      <div className="w-full max-h-112 overflow-auto rounded-md border border-solid border-neutral-border">
        {activeRates.length > 0 && (
          <>
            {sectionHeader("Active — FY 2026-27 (§393)", activeRates.length)}
            {activeRates.map(renderRate)}
          </>
        )}
        {expiredRates.length > 0 && (
          <>
            {sectionHeader("Expired — pre FY 2026-27", expiredRates.length)}
            {expiredRates.map(renderRate)}
          </>
        )}
        {!loading && rates.length === 0 && (
          <div className="flex w-full items-center justify-center px-4 py-6">
            <span className="text-body font-body text-subtext-color">
              No TDS rates found. Run Sync TDS first.
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Standalone card wrapper around TdsCurationList (the Sync tab reaches the list
 * through the Manage modal on TdsSyncCard; this wrapper is kept for reuse).
 */
export const TdsCurationCard: React.FC = () => (
  <div className="flex w-full flex-col items-start gap-4 rounded-lg border border-solid border-neutral-border bg-default-background p-6">
    <div className="flex flex-col items-start gap-1">
      <span className="text-heading-3 font-heading-3 text-default-font">
        TDS Rate Curation
      </span>
      <span className="text-body font-body text-subtext-color">
        Create, edit and curate TDS rates. Names, rates and sections write
        through to Zoho Books.
      </span>
    </div>
    <TdsCurationList />
  </div>
);

export default TdsCurationCard;
